import { memo } from 'react';
import { slugify } from '../../utils/slugify';
import {
  TableEmptyState,
  TableNoResults,
} from './components/table-empty-state';
import { TableHeadRow } from './components/table-head-row';
import { TablePagination } from './components/table-pagination';
import { TableRow } from './components/table-row';
import { useTable } from './hooks/use-table';
import { StyledTable, TableWrapper } from './table.styles';
import type { TableProps } from './types';

export const Table = <T extends object>(props: TableProps<T>) => {
  const {
    table,
    isNarrow,
    showNoResults,
    emptyState,
    enableHorizontalScroll,
    ariaLabel,
    hidePageSizeDropdown,
    onRowClick,
    showEmptyState,
    showPagination,
    pageIndex,
    pageSize,
    totalItems,
    pageCount,
    currentRowsCount,
    onChangePage,
    onChangePageSize,
  } = useTable(props);

  if (showNoResults) {
    return <TableNoResults />;
  }

  if (showEmptyState) {
    return <TableEmptyState {...emptyState} />;
  }
  return (
    <TableWrapper enableHorizontalScroll={enableHorizontalScroll}>
      <StyledTable
        aria-label={ariaLabel}
        data-testid={`${slugify(ariaLabel)}-table`}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeadRow key={headerGroup.id} headerGroup={headerGroup} />
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} row={row} onClick={onRowClick} />
          ))}
        </tbody>
      </StyledTable>
      {showPagination && (
        <TablePagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalItems={totalItems}
          pageCount={pageCount}
          currentRowsCount={currentRowsCount}
          onChangePage={onChangePage}
          onChangePageSize={onChangePageSize}
          hidePageSizeDropdown={hidePageSizeDropdown}
          isNarrow={isNarrow}
        />
      )}
    </TableWrapper>
  );
};

export default memo(Table) as typeof Table;
